import React, { useState, useEffect } from "react";
import { Icon } from "../../icons.jsx";
import { Card, Button, Badge, Field, Modal } from "../components.jsx";
import { useStore } from "../store.jsx";
import {
  CATEGORIAS_MINISTERIO, SUBGRUPOS_MINISTERIO, SUBFACTORES_MINISTERIO,
  textosDeSubfactor, puntosDeSubfactor, otroCompleto, conDosDecimales,
} from "../ministerio.js";

import { PageHead } from "./dashboard.jsx";

/* ============================================================
   Talention — Factores del modelo Ministerio
   Catálogo oficial de solo lectura (pesos, máximos y niveles) y
   los tres factores «Otros» que la empresa puede definir y activar.
   ============================================================ */

const pct = (n) => `${conDosDecimales(n * 100)} %`;
const maximo = (sf) => conDosDecimales(puntosDeSubfactor(sf, sf.opciones));

function FactoresMinisterio({ selectorMetodo }) {
  return (
    <div className="stack" style={{ gap: 22 }}>
      <PageHead
        title="Factores de Valoración"
        sub="Catálogo oficial de la Herramienta SVPT del Ministerio. Los pesos y los niveles son fijos; solo se pueden definir y activar los factores «Otros»."
      />
      {selectorMetodo}
      {CATEGORIAS_MINISTERIO.map(c => (
        <Card key={c.id} eyebrow={`Categoría ${c.id} · ${pct(c.peso)}`} title={c.nombre}>
          <div className="stack" style={{ gap: 16 }}>
            {SUBGRUPOS_MINISTERIO.filter(g => g.categoria === c.id).map(g => (
              <div key={g.id}>
                <div className="card-eyebrow" style={{ marginBottom: 8 }}>{g.nombre}</div>
                <div className="stack" style={{ gap: 6 }}>
                  {SUBFACTORES_MINISTERIO.filter(sf => sf.subgrupo === g.id).map(sf => (
                    sf.otros
                      ? <OtroCard key={sf.codigo} sf={sf} />
                      : <SubfactorFila key={sf.codigo} sf={sf} categoria={c} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

function SubfactorFila({ sf, categoria }) {
  const [abierto, setAbierto] = useState(false);
  const textos = textosDeSubfactor(sf.codigo);
  return (
    <div role="group" aria-label={`${sf.codigo} ${sf.nombre}`} className="surface-soft" style={{ padding: "10px 14px" }}>
      <div className="row" style={{ gap: 10, alignItems: "flex-start" }}>
        <span className="mono semi" style={{ fontSize: 12, minWidth: 44 }}>{sf.codigo}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="semi" style={{ fontSize: 13.5 }}>{sf.nombre}</div>
          <div className="tiny muted">
            {pct(sf.pesoEfectivo / categoria.peso)} de la categoría · {pct(sf.pesoEfectivo)} efectivo · máx. {maximo(sf)} pts
          </div>
          {sf.nota && (
            <div className="tiny" style={{ marginTop: 4, color: "#92400e", display: "flex", gap: 6, alignItems: "center" }}>
              <Icon name="alert" size={12} /> {sf.nota}
            </div>
          )}
        </div>
        {sf.tipo === "matriz" && <Badge>Matriz</Badge>}
        <Button size="sm" variant="ghost" onClick={() => setAbierto(a => !a)}>
          {abierto ? "Ocultar niveles" : "Ver niveles"}
        </Button>
      </div>
      {abierto && textos && (
        <div style={{ marginTop: 10, paddingLeft: 4 }}>
          {textos.definicion && <p className="muted" style={{ fontSize: 12.5, margin: "0 0 8px" }}>{textos.definicion}</p>}
          <ul className="stack" style={{ gap: 4, margin: 0, padding: 0, listStyle: "none" }}>
            {textos.niveles.map((t, i) => <li key={i} style={{ fontSize: 12.5 }}>{t}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

/* Lo que se guarda de un «Otros»: los textos recortados, para que uno de
   solo espacios cuente como vacío. */
const limpio = (o) => ({
  nombre: o.nombre.trim(),
  justificacion: o.justificacion.trim(),
  niveles: o.niveles.map(t => t.trim()),
});
const borradorDe = (o) => ({ nombre: o.nombre, justificacion: o.justificacion, niveles: [...o.niveles] });

function OtroCard({ sf }) {
  const { state, dispatch } = useStore();
  const otro = state.ministerio.otros[sf.codigo];
  const [borrador, setBorrador] = useState(() => borradorDe(otro));
  const [confirmando, setConfirmando] = useState(false);
  // Tras guardar, activar o desactivar, el borrador vuelve a lo guardado.
  useEffect(() => { setBorrador(borradorDe(otro)); }, [otro]);

  const completo = otroCompleto(borrador);
  const cambiado = JSON.stringify(limpio(borrador)) !== JSON.stringify(limpio(otro));
  const conRespuesta = state.positions
    .filter(p => state.ministerio.respuestas[p.id]?.[sf.codigo] !== undefined).length;

  const setNivel = (i, valor) => setBorrador(b => ({ ...b, niveles: b.niveles.map((t, j) => (j === i ? valor : t)) }));
  const guardar = () => dispatch({ type: "SET_OTRO_MINISTERIO", codigo: sf.codigo, patch: limpio(borrador) });
  const desactivar = () => {
    dispatch({ type: "SET_OTRO_MINISTERIO", codigo: sf.codigo, patch: { activo: false } });
    setConfirmando(false);
  };
  const pulsarInterruptor = () => {
    if (!otro.activo) dispatch({ type: "SET_OTRO_MINISTERIO", codigo: sf.codigo, patch: { ...limpio(borrador), activo: true } });
    else if (conRespuesta > 0) setConfirmando(true);
    else desactivar();
  };

  return (
    <div role="group" aria-label={`${sf.codigo} Otros`} className="surface-soft" style={{ padding: 14 }}>
      <div className="row" style={{ gap: 10, alignItems: "center", marginBottom: 12 }}>
        <span className="mono semi" style={{ fontSize: 12, minWidth: 44 }}>{sf.codigo}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="semi" style={{ fontSize: 13.5 }}>{otro.nombre || "Otros"}</div>
          <div className="tiny muted">Factor propio de la empresa · {pct(sf.pesoEfectivo)} efectivo · máx. {maximo(sf)} pts</div>
        </div>
        <Badge tone={otro.activo ? "good" : ""}>{otro.activo ? "Activo" : "Inactivo"}</Badge>
        <button
          type="button"
          className={"switch " + (otro.activo ? "on" : "")}
          role="switch"
          aria-checked={otro.activo}
          aria-label={`Activar ${sf.codigo}`}
          disabled={!otro.activo && !completo}
          title={!otro.activo && !completo ? "Rellena el nombre y los 5 niveles para activarlo" : undefined}
          onClick={pulsarInterruptor}
        />
      </div>
      <div className="stack" style={{ gap: 10 }}>
        <Field label="Nombre">
          <input className="input" value={borrador.nombre} placeholder="Ej. Trabajo a turnos"
                 onChange={e => setBorrador(b => ({ ...b, nombre: e.target.value }))} />
        </Field>
        <Field label="Justificación">
          <textarea className="textarea" rows={2} value={borrador.justificacion}
                    onChange={e => setBorrador(b => ({ ...b, justificacion: e.target.value }))} />
        </Field>
        {borrador.niveles.map((t, i) => (
          <Field key={i} label={`Nivel ${i + 1}`}>
            <input className="input" value={t} onChange={e => setNivel(i, e.target.value)} />
          </Field>
        ))}
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <Button size="sm" onClick={guardar} disabled={!cambiado || (otro.activo && !completo)}
            title={otro.activo && !completo ? "Un factor activo necesita nombre y los 5 niveles" : undefined}>
            Guardar
          </Button>
        </div>
      </div>

      <Modal
        open={confirmando}
        onClose={() => setConfirmando(false)}
        title={`Desactivar ${otro.nombre || sf.codigo}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmando(false)}>Cancelar</Button>
            <Button variant="primary" onClick={desactivar}>Desactivar</Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          {conRespuesta === 1 ? "1 puesto tiene" : `${conRespuesta} puestos tienen`} respuesta en este factor. Al desactivarlo se borran esas respuestas.
        </p>
      </Modal>
    </div>
  );
}

export { FactoresMinisterio };
